import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/CarSettlements.jsx
import React, { useEffect, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import usePersistedUI from "../hooks/usePersistedUI"; // ✅ 추가
import { getCarNumberMatchKey } from "../utils/dispatchConflictUtils";

// 날짜 유틸
const startOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

// 지출항목 키(숫자 필드) - cars-expenses 테이블과 일치
const EXPENSE_NUMERIC_KEYS = [
  "johab","unyoung","gps","halbu","geubyeo","4bohum",
  "tonghaeng","joocha","sooksic","jungbi","jooyu",
  "bohum","secha","chooljang","gwatae","gita"
];
const normalizeDriverName = (value = "") => String(value || "").trim().replace(/\s+/g, " ");
const normalizeSettlementCarKey = (value = "") => getCarNumberMatchKey(value) || String(value || "(미지정)").trim();
const makeSettlementKey = (carNumber, driverName) =>
  `${normalizeSettlementCarKey(carNumber)}__${normalizeDriverName(driverName)}`;
const getExpenseMonth = (row = {}) => String(row.date || "").slice(0, 7);
const isInstallmentAutoExpense = (row = {}) => String(row.bigo || "").startsWith("할부 내역 자동 등록");
const isGeneralAutomationExpense = (row = {}) => String(row.bigo || "").startsWith("지출 자동화");
const getMonthlyCarExpenseKey = (row = {}) =>
  `${normalizeSettlementCarKey(row.car_number)}__${getExpenseMonth(row)}`;

async function fetchAllPages(buildQuery, pageSize = 1000) {
  const rows = [];

  for (let from = 0; ; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;

    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }

  return rows;
}

export default function CarSettlements() {
  // ❌ 기존: useState로 직접 관리
  // const [dateRange, setDateRange] = useState({ from: startOfMonth(), to: today() });
  // const [filters, setFilters] = useState({ car_number: "", human_name: "" });

  // ✅ 공용 훅으로 상태 관리 + sessionStorage + URL 동기화
  const ui = usePersistedUI(
    "car-settlements",
    {
      dateRange: { from: startOfMonth(), to: today() },
      // filters 키는 다른 화면과 통일해서 사용(차량/기사)
      filters: { 차량: "", 기사: "" },
    },
    { urlSync: true } // URL ?from=...&to=...&car=...&driver=... 동기화
  );
  const { state } = ui;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);           // 표 데이터(차량별)
  const [totals, setTotals] = useState({          // 전체 합계
    sales: 0, expense: 0, net: 0,
  });
  const navigate = useNavigate();

  // 회사 범위(이 앱에선 email을 company로 쓰고 있으므로 동일하게)
  const company = localStorage.getItem("email") || "";

  // 데이터 조회
  const fetchData = async () => {
    if (!company) {
      showUserAlert("회사 식별 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }
    setLoading(true);
    try {
      // 공통 필터
      const { from, to } = state.dateRange || {};
      const likeCar = state.filters?.차량?.trim() || "";
      const likeHuman = state.filters?.기사?.trim() || "";

      // 1) 매출(=reservations)
      const buildSalesQuery = () => {
        let query = supabase
          .from("reservations")
          .select("id, date, car_number, money, company, human_name")
          .eq("company", company)
          .order("date", { ascending: true })
          .order("id", { ascending: true });
        if (from) query = query.gte("date", from);
        if (to) query = query.lte("date", to);
        if (likeCar) query = query.ilike("car_number", `%${likeCar}%`);
        if (likeHuman) query = query.ilike("human_name", `%${likeHuman}%`);
        return query;
      };
      const sales = await fetchAllPages(buildSalesQuery);

      // 2) 지출(=cars-expenses)
      const buildExpenseQuery = () => {
        let query = supabase
          .from("cars-expenses")
          .select("*")
          .eq("company", company)
          .order("date", { ascending: true })
          .order("id", { ascending: true });
        if (from) query = query.gte("date", from);
        if (to) query = query.lte("date", to);
        if (likeCar) query = query.ilike("car_number", `%${likeCar}%`);
        if (likeHuman) query = query.ilike("human_name", `%${likeHuman}%`);
        return query;
      };
      const exps = await fetchAllPages(buildExpenseQuery);

      // 3) 차량 → 기사명 매핑(보조) : cars 테이블
      const driversMap = {};
      const { data: cars } = await supabase
        .from("cars")
        .select("car_number, car_number_only, driver")
        .eq("company", company);
      if (Array.isArray(cars)) {
        cars.forEach((c) => {
          if (c?.car_number) driversMap[normalizeSettlementCarKey(c.car_number)] = c.driver || "";
          if (c?.car_number_only) driversMap[normalizeSettlementCarKey(c.car_number_only)] = c.driver || "";
        });
      }

      // 4) 차량별 집계
      const byCar = new Map();
      // 차량별 '출근일'(고유 운행일) 계산용 Set
      const daysByCar = new Map(); // car_number -> Set<YYYY-MM-DD>

      const driverForCar = (carNum) => {
        return driversMap[normalizeSettlementCarKey(carNum)] || "";
      };

      const ensure = (carNum, driverName = "") => {
        const nextDriverName = normalizeDriverName(driverName) || driverForCar(carNum);
        const key = makeSettlementKey(carNum, nextDriverName);
        if (!byCar.has(key)) {
          const base = {
            car_number: carNum,
            car_key: normalizeSettlementCarKey(carNum),
            human_name: nextDriverName,
            sales: 0,
            expense: 0,
            net: 0,
            workdays: 0,  // 출근일(고유 운행일 수)
            note: "",
          };
          // 지출 항목별 금액도 0으로 초기화 (NaN 방지용)
          EXPENSE_NUMERIC_KEYS.forEach((k) => {
            base[k] = 0;
          });
          byCar.set(key, base);
        }
        return byCar.get(key);
      };

      // 매출 누적
      for (const s of sales || []) {
        const car = s.car_number || "(미지정)";
        const row = ensure(car, s.human_name);
        row.sales += Number(s.money) || 0;

        // 출근일: 날짜 Set에 추가
        const d = (s.date || "").slice(0, 10); // 'YYYY-MM-DD'
        if (d) {
          const key = makeSettlementKey(car, row.human_name);
          if (!daysByCar.has(key)) daysByCar.set(key, new Set());
          daysByCar.get(key).add(d);
        }
      }

      // 지출 누적
      const installmentAutoExpenseKeys = new Set(
        (exps || [])
          .filter((row) => isInstallmentAutoExpense(row) && Number(row.halbu || 0) > 0)
          .map(getMonthlyCarExpenseKey)
      );
      for (const e of exps || []) {
        const car = e.car_number || "(미지정)";
        const row = ensure(car, e.human_name);
        // 총 지출 = 모든 숫자항목 합
        for (const k of EXPENSE_NUMERIC_KEYS) {
          if (
            k === "halbu" &&
            isGeneralAutomationExpense(e) &&
            installmentAutoExpenseKeys.has(getMonthlyCarExpenseKey(e))
          ) {
            continue;
          }
          row[k] += Number(e[k]) || 0;
          row.expense += Number(e[k]) || 0;
        }
      }

      // 기사명 보정: cars 매핑으로 보완
      for (const row of byCar.values()) {
        // 출근일 계산(기간 내 고유 운행일 수)
        row.workdays = daysByCar.get(makeSettlementKey(row.car_number, row.human_name))?.size || 0;
        row.net = row.sales - row.expense;
      }

      // 배열로 변환 & 정렬(차량번호 오름차순)
      const out = Array.from(byCar.values()).sort((a, b) =>
        String(a.car_number).localeCompare(String(b.car_number), "ko-KR", { numeric: true }) ||
        String(a.human_name).localeCompare(String(b.human_name), "ko-KR", { numeric: true })
      );
      setRows(out);

      // 전체 합계
      const t = out.reduce(
        (acc, r) => {
          acc.sales += r.sales;
          acc.expense += r.expense;
          acc.net += r.net;
          return acc;
        },
        { sales: 0, expense: 0, net: 0 }
      );
      setTotals(t);
    } catch (e) {
      console.error(e);
      showUserAlert("정산 내역 조회 중 오류: " + (e.message || e));
      setRows([]);
      setTotals({ sales: 0, expense: 0, net: 0 });
    } finally {
      setLoading(false);
    }
  };

  // ✅ 기간/필터 변경 시 자동 조회
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dateRange.from, state.dateRange.to, state.filters.차량, state.filters.기사]);

  const number = (v) => (Number(v) || 0).toLocaleString();

  // 스크롤 내려도 헤더 보이게(sticky)
  const TableWrapper = ({ children }) => (
    <div className="relative max-h-[70vh] overflow-auto">{children}</div>
  );

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-3">
        <h2 className="text-lg font-semibold">정산 내역</h2>

        {/* 필터 */}
        <div className="flex flex-wrap items-end gap-2">
          <span className="flex h-10 items-center text-base font-medium">기간</span>
          <Input
            type="date"
            {...ui.bind("dateRange.from")}
          />
          <span>~</span>
          <Input
            type="date"
            {...ui.bind("dateRange.to")}
          />
          <Input
            placeholder="차량번호"
            className="w-40"
            {...ui.bind("filters.차량")}
          />
          <Input
            placeholder="기사명"
            className="w-40"
            {...ui.bind("filters.기사")}
          />
          <Button onClick={fetchData} disabled={loading}>
            {loading ? "조회중..." : "검색"}
          </Button>
        </div>

        <Card>
          <CardContent className="bg-white">
            <TableWrapper>
              <table className="table-fixed w-full border text-sm">
                <colgroup>
                  <col className="w-14" />
                  <col className="w-[86px]" />
                  <col className="w-[76px]" />
                  <col className="w-16" />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead className="sticky top-0 z-10 bg-gray-100">
                  <tr className="font-semibold">
                    <th className="border p-2 text-center">순번</th>
                    <th className="border p-2 text-center">차량번호</th>
                    <th className="border p-2 text-center">기사명</th>
                    <th className="border p-2 text-center">출근일</th>
                    <th className="border p-2 text-right">매출합</th>
                    <th className="border p-2 text-right">지출합</th>
                    <th className="border p-2 text-right">합계</th>
                    <th className="border p-2 text-center">비고</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 합계 행 */}
                  <tr className="bg-gray-200 font-semibold">
                    <td className="border p-2 text-center">합계</td>
                    <td className="border p-2 text-center" colSpan={2}>전체</td>
                    <td className="border p-2 text-center">
                      {rows.reduce((acc, r) => acc + (r.workdays || 0), 0).toLocaleString()}
                    </td>
                    <td className="border p-2 text-right">{number(totals.sales)}</td>
                    <td className="border p-2 text-right">{number(totals.expense)}</td>
                    <td className="border p-2 text-right">{number(totals.net)}</td>
                    <td className="border p-2 text-center"></td>
                  </tr>

                  {/* 차량별 행 */}
                  {rows.map((r, idx) => (
                    <tr key={r.car_number || idx}>
                      <td className="border p-2 text-center">{idx + 1}</td>
                      <td className="border p-2 text-center">
                        <button
                          className="text-blue-600 underline"
                          title="차량별 정산 상세 보기"
                          onClick={() =>
                            navigate(
                              `/settlements/${encodeURIComponent(r.car_number)}?from=${state.dateRange.from}&to=${state.dateRange.to}`,
                              { state: { human_name: r.human_name, sales: r.sales, expense: r.expense, net: r.net } }
                            )
                          }
                        >
                          {r.car_number}
                        </button>
                      </td>
                      <td className="border p-2 text-center">{r.human_name}</td>
                      <td className="border p-2 text-center">
                        {(r.workdays || 0).toLocaleString()}
                      </td>
                      <td className="border p-2 text-right">{number(r.sales)}</td>
                      <td className="border p-2 text-right">{number(r.expense)}</td>
                      <td className="border p-2 text-right">{number(r.net)}</td>
                      <td className="border p-2 text-center">{r.note || ""}</td>
                    </tr>
                  ))}

                  {rows.length === 0 && !loading && (
                    <tr>
                      <td className="border p-4 text-center text-gray-500" colSpan={8}>
                        데이터가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableWrapper>
          </CardContent>
        </Card>

        <p className="text-xs text-gray-500">
          * 매출합: reservations.money 합계 / 지출합: cars-expenses의 모든 비용 항목 합계 (동일 기간·회사·차량·기사 기준)
        </p>
      </div>
    </div>
  );
}
